import type { ComponentPropsWithRef, ReactNode } from 'react'
import cx from 'classnames'
import { match } from '@/utils/pattern-match-util'
import styles from './RadioButtonGroup.module.css'

/**
 * Radio Button Group Size.
 */
export type RadioButtonGroupSize = 'big' | 'big-wide' | 'medium'

export interface RadioButtonGroupProps {
  children: ReactNode
  className?: string
  size?: RadioButtonGroupSize
  /**
   * Useful when used in a Form.
   */
  label?: string
}

/**
 * A group of radio buttons, with an optional label.
 */
export function RadioButtonGroup({
  size = 'big',
  className,
  children,
  label,
}: RadioButtonGroupProps) {
  return (
    <>
      {label && <div className={styles.radioButtonGroupLabel}>{label}</div>}
      <div
        className={cx(
          styles.radioButtonGroup,
          match(size, {
            big: styles.big,
            'big-wide': styles['big-wide'],
            medium: styles.medium,
          }),
          className,
        )}
      >
        {children}
      </div>
    </>
  )
}

export interface RadioButtonItemProps extends Omit<
  ComponentPropsWithRef<'button'>,
  'onClick' | 'disabled'
> {
  children: ReactNode
  isDisabled?: boolean
  active: boolean
  onPress?: () => unknown
  testId?: string
}

/**
 * A button in a radio button group.
 */
export function RadioButtonItem({
  children,
  className,
  testId,
  active,
  onPress,
  isDisabled,
  ref,
  ...rest
}: RadioButtonItemProps) {
  return (
    <button
      disabled={isDisabled}
      ref={ref}
      data-test={testId}
      onClick={isDisabled ? undefined : onPress}
      type="button"
      className={cx(
        styles.radioButtonGroupItem,
        active && styles.active,
        isDisabled && styles.disabled,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
